<template>
  <div>
    <!-- Header con título y selector de periodo -->
    <div class="d-flex justify-content-between align-items-center mb-2">
      <h4 class="mb-0">
        <feather-icon
          icon="BarChart2Icon"
          size="20"
          class="mr-50"
        />
        Estadísticas del Asistente IA
      </h4>
      <b-form-select
        v-model="period"
        :options="periodOptions"
        size="sm"
        style="width: 160px"
        @change="loadAll"
      />
    </div>

    <!-- Summary cards -->
    <b-row class="mb-2">
      <b-col md="3">
        <b-card class="text-center mb-1">
          <feather-icon
            icon="MessageSquareIcon"
            size="24"
            class="text-primary mb-50"
          />
          <h2 class="text-primary mb-0">
            {{ summary.total_messages || 0 }}
          </h2>
          <small class="text-muted">
            Mensajes · {{ summary.unique_users || 0 }} usuarios
          </small>
        </b-card>
      </b-col>
      <b-col md="3">
        <b-card class="text-center mb-1">
          <feather-icon
            icon="CpuIcon"
            size="24"
            class="text-info mb-50"
          />
          <h2 class="text-info mb-0">
            {{ formatNumber(summary.total_tokens || 0) }}
          </h2>
          <small class="text-muted">Tokens totales</small>
        </b-card>
      </b-col>
      <b-col md="3">
        <b-card class="text-center mb-1">
          <feather-icon
            icon="DollarSignIcon"
            size="24"
            class="text-success mb-50"
          />
          <h2 class="text-success mb-0">
            ${{ formatCost(summary.total_cost || 0) }}
          </h2>
          <small class="text-muted">Costo estimado</small>
        </b-card>
      </b-col>
      <b-col md="3">
        <b-card class="text-center mb-1">
          <feather-icon
            icon="ClockIcon"
            size="24"
            class="text-warning mb-50"
          />
          <h2 class="text-warning mb-0">
            {{ formatNumber(summary.avg_response_time_ms || 0) }}
          </h2>
          <small class="text-muted">ms promedio de respuesta</small>
        </b-card>
      </b-col>
    </b-row>

    <!-- Feedback card -->
    <b-row class="mb-2">
      <b-col md="6">
        <b-card>
          <h6 class="mb-1">
            <feather-icon
              icon="ThumbsUpIcon"
              size="16"
              class="mr-50 text-success"
            />
            Feedback de usuarios
          </h6>
          <div
            v-if="summary.feedback && summary.feedback.total > 0"
          >
            <div class="d-flex justify-content-between mb-25">
              <small class="text-muted">
                Total: {{ summary.feedback.total }} respuestas evaluadas
              </small>
              <small>
                <span class="text-success mr-50">
                  <feather-icon
                    icon="ThumbsUpIcon"
                    size="12"
                  />
                  {{ summary.feedback.positive }}
                </span>
                <span class="text-danger">
                  <feather-icon
                    icon="ThumbsDownIcon"
                    size="12"
                  />
                  {{ summary.feedback.negative }}
                </span>
              </small>
            </div>
            <b-progress
              :value="feedbackPositivePercent"
              max="100"
              variant="success"
              height="8px"
            />
            <small class="text-muted d-block mt-25">
              {{ feedbackPositivePercent }}% positivo
            </small>
          </div>
          <div v-else>
            <small class="text-muted">Sin datos de feedback en el período seleccionado.</small>
          </div>
        </b-card>
      </b-col>
      <b-col md="6">
        <!-- Herramientas más usadas -->
        <b-card>
          <h6 class="mb-1">
            <feather-icon
              icon="ToolIcon"
              size="16"
              class="mr-50 text-primary"
            />
            Herramientas más usadas
          </h6>
          <b-table
            :items="toolStats"
            :fields="toolFields"
            :busy="loadingTools"
            small
            responsive
            show-empty
            empty-text="Sin datos para el período"
          >
            <template #table-busy>
              <div class="text-center py-1">
                <b-spinner
                  small
                  variant="primary"
                />
              </div>
            </template>
            <template #cell(tool)="data">
              {{ toolLabels[data.item.tool] || data.item.tool }}
            </template>
          </b-table>
        </b-card>
      </b-col>
    </b-row>

    <!-- Tabla de uso por usuario -->
    <b-card>
      <h6 class="mb-1">
        <feather-icon
          icon="UsersIcon"
          size="16"
          class="mr-50 text-primary"
        />
        Uso por usuario
      </h6>
      <b-table
        :items="userStats"
        :fields="userFields"
        :busy="loadingUsers"
        responsive
        show-empty
        empty-text="Sin datos para el período"
        sort-icon-left
      >
        <template #table-busy>
          <div class="text-center py-2">
            <b-spinner variant="primary" />
          </div>
        </template>
        <template #cell(role)="data">
          <b-badge :variant="data.item.role === 'admin' ? 'light-danger' : 'light-primary'">
            {{ data.item.role === 'admin' ? 'Admin' : 'Vendedor' }}
          </b-badge>
        </template>
        <template #cell(total_cost)="data">
          ${{ formatCost(data.item.total_cost) }}
        </template>
        <template #cell(avg_response_time)="data">
          {{ formatNumber(data.item.avg_response_time) }} ms
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import axios from '@axios'
import {
  BRow,
  BCol,
  BCard,
  BTable,
  BBadge,
  BProgress,
  BSpinner,
  BFormSelect,
} from 'bootstrap-vue'

export default {
  name: 'ChatStats',

  components: {
    BRow,
    BCol,
    BCard,
    BTable,
    BBadge,
    BProgress,
    BSpinner,
    BFormSelect,
  },

  data() {
    return {
      period: 30,
      periodOptions: [
        { value: 7, text: 'Últimos 7 días' },
        { value: 15, text: 'Últimos 15 días' },
        { value: 30, text: 'Últimos 30 días' },
        { value: 60, text: 'Últimos 60 días' },
        { value: 90, text: 'Últimos 90 días' },
      ],
      summary: {},
      userStats: [],
      toolStats: [],
      loadingUsers: false,
      loadingTools: false,
      userFields: [
        { key: 'name', label: 'Usuario', sortable: true },
        { key: 'role', label: 'Rol', sortable: true },
        { key: 'total_messages', label: 'Mensajes', sortable: true },
        { key: 'total_tokens', label: 'Tokens', sortable: true },
        { key: 'total_cost', label: 'Costo', sortable: true },
        { key: 'avg_response_time', label: 'T. Promedio', sortable: true },
      ],
      toolFields: [
        { key: 'tool', label: 'Herramienta' },
        { key: 'count', label: 'Usos', sortable: true },
      ],
      toolLabels: {
        search_orders: 'Buscar pedidos',
        get_order_detail: 'Detalle de pedido',
        search_quotes: 'Buscar cotizaciones',
        get_quote_detail: 'Detalle de cotización',
        search_products: 'Buscar productos',
        check_stock: 'Verificar stock',
        search_customers: 'Buscar clientes',
        get_customer_detail: 'Detalle de cliente',
      },
    }
  },

  computed: {
    feedbackPositivePercent() {
      const fb = this.summary.feedback
      if (!fb || !fb.total) return 0
      return Math.round((fb.positive / fb.total) * 100)
    },
  },

  created() {
    this.loadAll()
  },

  methods: {
    async loadAll() {
      await Promise.all([
        this.loadSummary(),
        this.loadUserStats(),
        this.loadToolStats(),
      ])
    },

    async loadSummary() {
      try {
        const { data } = await axios.get('system/chat-stats/summary', {
          params: { period: this.period },
        })
        if (data.res) {
          this.summary = data.data
        }
      } catch (error) {
        this.showErrors(error)
      }
    },

    async loadUserStats() {
      try {
        this.loadingUsers = true
        const { data } = await axios.get('system/chat-stats/by-user', {
          params: { period: this.period },
        })
        if (data.res) {
          this.userStats = data.data
        }
      } catch (error) {
        this.showErrors(error)
      } finally {
        this.loadingUsers = false
      }
    },

    async loadToolStats() {
      try {
        this.loadingTools = true
        const { data } = await axios.get('system/chat-stats/by-tool', {
          params: { period: this.period },
        })
        if (data.res) {
          this.toolStats = data.data
        }
      } catch (error) {
        this.showErrors(error)
      } finally {
        this.loadingTools = false
      }
    },

    formatNumber(value) {
      if (!value) return 0
      return Number(value).toLocaleString('es-GT')
    },

    formatCost(value) {
      if (!value) return '0.0000'
      return Number(value).toFixed(4)
    },
  },
}
</script>

<style lang="scss" scoped>
</style>
